<template>
  <div class="tw:flex tw:flex-col tw:w-full">
    <div
      :class="[
        'textarea-v2 tw:relative tw:flex tw:w-full tw:rounded-[8px] tw:border tw:transition-all tw:duration-150',
        sizeClass,
        stateClasses,
        { 'skeleton-play': skeleton },
      ]"
      @mouseenter="hovered = true"
      @mouseleave="hovered = false"
    >
      <textarea
        :id="id"
        ref="textareaRef"
        v-model="internalValue"
        :class="[
          'tw:flex-1 tw:min-w-0 tw:bg-transparent tw:outline-none tw:border-none tw:text-(--fontcolor)',
          'tw:placeholder-(--placeholder-color) tw:resize-y',
          { 'tw:cursor-not-allowed tw:opacity-50': disabled },
        ]"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxlength"
        :rows="rows"
        :name="name"
        :data-testid="id"
        @focus="focused = true"
        @blur="focused = false"
        @keydown.enter="emit('enter')"
      />
    </div>
    <span
      v-if="maxlength"
      class="tw:text-xs tw:text-(--text-color) tw:opacity-60 tw:self-end tw:mt-1"
    >{{ String(internalValue || '').length }}/{{ maxlength }}</span>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

const props = withDefaults(defineProps<{
  modelValue?: string | number
  id?: string
  name?: string
  placeholder?: string
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  readonly?: boolean
  skeleton?: boolean
  error?: boolean
  maxlength?: number | string
  rows?: number | string
}>(), {
  modelValue: undefined,
  id: undefined,
  name: undefined,
  placeholder: undefined,
  size: 'md',
  disabled: false,
  readonly: false,
  skeleton: false,
  error: false,
  maxlength: undefined,
  rows: 4,
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number | undefined]
  'enter': []
}>()

const textareaRef = ref<HTMLTextAreaElement | null>(null)
const focused = ref(false)
const hovered = ref(false)

const internalValue = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})

// --- Size classes (matching InputPlayV3 pattern) ---
const sizeClass = computed(() => ({
  sm: 'tw:p-2 tw:text-xs',
  md: 'tw:p-3 tw:text-sm',
  lg: 'tw:p-4 tw:text-base',
}[props.size]))

// --- State classes ---
const stateClasses = computed(() => {
  if (props.disabled) return 'tw:border-(--input-border-color) tw:bg-(--bg-input) tw:opacity-60 tw:cursor-not-allowed'
  if (props.error) return 'tw:border-(--feedback-error-500) tw:bg-(--bg-input)'
  if (focused.value) return 'tw:border-(--fontcolor) tw:bg-(--bg-input) tw:ring-2 tw:ring-(--fontcolor)/10'
  if (hovered.value) return 'tw:border-(--fontcolor) tw:bg-(--bg-input)'
  return 'tw:border-(--input-border-color) tw:bg-(--bg-input)'
})

defineExpose({ textareaRef, focus: () => textareaRef.value?.focus() })
</script>

<style scoped>
.textarea-v2.skeleton-play > * {
  opacity: 0;
}

.textarea-v2 textarea:-webkit-autofill {
  -webkit-box-shadow: 0 0 0 1000px var(--bg-input) inset !important;
  -webkit-text-fill-color: var(--fontcolor) !important;
}

.textarea-v2 textarea::placeholder {
  color: var(--placeholder-color) !important;
}
</style>
